// ---------- Facturation MILES — composants partagés (devis & factures) ----------
import { useState } from "react";
import { ChevronDown, Plus, Search, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { inputCls, money } from "./ui";

export const PAYMENT_METHODS = {
  bank_transfer: "Virement bancaire", swiss_qr: "Swiss QR", card_stripe: "Carte / Stripe",
  cash: "Cash", credit_note: "Avoir", other: "Autre",
};

export const emptyLine = () => ({
  description: "", qty: 1, unit_price: "", vat_rate: null, discount_type: null, discount_value: 0,
});

const r2 = (v) => Math.round(v * 100) / 100;

// miroir du moteur backend (indicatif — le serveur recalcule en Decimal)
export function computePreview(lines, vatRegistered, defaultRate) {
  let net = 0, vat = 0, discount = 0;
  const out = lines.map((l) => {
    const qty = Number(l.qty) || 0;
    const unit = r2(Number(l.unit_price) || 0);
    const gross = r2(qty * unit);
    let disc = 0;
    if (l.discount_type === "amount") disc = Math.min(r2(Number(l.discount_value) || 0), gross);
    if (l.discount_type === "percent") disc = r2(gross * Math.min(Number(l.discount_value) || 0, 100) / 100);
    const lineNet = r2(gross - disc);
    const rate = l.vat_rate === null || l.vat_rate === undefined || l.vat_rate === "" ? Number(defaultRate) : Number(l.vat_rate);
    const lineVat = vatRegistered ? r2(lineNet * rate / 100) : 0;
    net = r2(net + lineNet); vat = r2(vat + lineVat); discount = r2(discount + disc);
    return { ...l, _gross: gross, _disc: disc, _net: lineNet, _vat: lineVat, _total: r2(lineNet + lineVat), _rate: rate };
  });
  const total = Math.round(r2(net + vat) * 20) / 20; // 5 rappen
  return { lines: out, gross: r2(net + discount), net, vat, discount, total };
}

export function PartyFields({ value, onChange, testId = "party" }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-3" data-testid={`${testId}-fields`}>
      <div>
        <label className="text-xs font-medium text-slate">Nom / Société *</label>
        <input value={value.name || ""} onChange={set("name")} data-testid={`${testId}-name`} className={`${inputCls} mt-1`} placeholder="Hôtel Président Wilson" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate">E-mail</label>
          <input type="email" value={value.email || ""} onChange={set("email")} data-testid={`${testId}-email`} className={`${inputCls} mt-1`} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate">Téléphone</label>
          <input value={value.phone || ""} onChange={set("phone")} data-testid={`${testId}-phone`} className={`${inputCls} mt-1`} />
        </div>
      </div>
      <div className="grid grid-cols-[1fr_90px] gap-3">
        <div>
          <label className="text-xs font-medium text-slate">Rue</label>
          <AddressAutocomplete value={value.street || ""} testId={`${testId}-street`} inputClass={`${inputCls} mt-1`}
            placeholder="Quai Wilson" onChange={(v) => onChange({ ...value, street: v })}
            selectValue={(item) => item.street || item.name || item.label}
            onSelect={(item) => item && onChange({
              ...value, street: item.street || item.name || value.street,
              house_num: item.housenumber || value.house_num,
              pcode: item.postcode || value.pcode,
              city: item.city || value.city,
              country: (item.countrycode || value.country || "CH").toUpperCase(),
            })} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate">N°</label>
          <input value={value.house_num || ""} onChange={set("house_num")} data-testid={`${testId}-housenum`} className={`${inputCls} mt-1`} placeholder="47" />
        </div>
      </div>
      <div className="grid grid-cols-[90px_1fr_70px] gap-3">
        <div>
          <label className="text-xs font-medium text-slate">NPA</label>
          <input value={value.pcode || ""} onChange={set("pcode")} data-testid={`${testId}-pcode`} className={`${inputCls} mt-1`} placeholder="1211" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate">Localité</label>
          <input value={value.city || ""} onChange={set("city")} data-testid={`${testId}-city`} className={`${inputCls} mt-1`} placeholder="Genève" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate">Pays</label>
          <input value={value.country || "CH"} onChange={set("country")} data-testid={`${testId}-country`} className={`${inputCls} mt-1 uppercase`} maxLength={2} />
        </div>
      </div>
    </div>
  );
}

// Liste clients avec recherche + regroupement alphabétique (A, B, C…)
export function CustomerPicker({ customers, onPick, testId = "customer-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const norm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const person = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim();
  const label = (c) => c.company || person(c);
  const sortKey = (c) => norm(c.first_name || c.last_name || c.company);
  const filtered = customers
    .filter((c) => !q || norm(`${person(c)} ${c.company || ""} ${c.email || ""} ${c.phone || ""}`).includes(norm(q)))
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b), "fr"));
  const groups = filtered.reduce((acc, c) => {
    const letter = (sortKey(c)[0] || "#").toUpperCase();
    (acc[letter] = acc[letter] || []).push(c);
    return acc;
  }, {});
  const letters = Object.keys(groups).sort((a, b) => a.localeCompare(b, "fr"));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className={`${inputCls} mt-1 flex items-center justify-between gap-2 text-left text-slate`}>
          <span>Choisir dans vos clients…</span>
          <ChevronDown className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] rounded-2xl border-ink/10 p-0">
        <div className="border-b border-ink/[0.06] p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate" strokeWidth={1.5} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…"
              data-testid={`${testId}-search`} className={`${inputCls} h-10 pl-9`} />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-1.5">
          {letters.map((letter) => (
            <div key={letter}>
              <p className="px-2.5 pb-1 pt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate"
                data-testid={`${testId}-group-${letter}`}>{letter}</p>
              {groups[letter].map((c) => (
                <button key={c.id} type="button" data-testid={`${testId}-option-${c.id}`}
                  onClick={() => { onPick(c); setOpen(false); setQ(""); }}
                  className="flex w-full items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-cloud">
                  <span className="text-sm font-medium text-ink">{label(c)}</span>
                  <span className="truncate text-xs text-slate">{c.company ? `${person(c)} · ${c.email || c.phone || ""}` : (c.email || c.phone || "")}</span>
                </button>
              ))}
            </div>
          ))}
          {letters.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-slate" data-testid={`${testId}-empty`}>Aucun client ne correspond</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LinesEditor({ lines, onChange, vatRegistered = true }) {
  const setLine = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i) => onChange(lines.filter((_, j) => j !== i));
  return (
    <div data-testid="lines-editor" className="space-y-2">
      {lines.map((line, i) => (
        <div key={i} className="rounded-xl border border-ink/[0.07] bg-white p-3" data-testid={`line-${i}`}>
          <div className="flex items-start gap-2">
            <input value={line.description} onChange={(e) => setLine(i, { description: e.target.value })}
              data-testid={`line-${i}-description`} className={`${inputCls} h-10 flex-1`}
              placeholder="Transport privé — Genève → Aéroport Zurich" />
            {lines.length > 1 && (
              <button type="button" onClick={() => remove(i)} data-testid={`line-${i}-remove`}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate transition-colors hover:bg-red-50 hover:text-red-600">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
          <div className="mt-2 grid grid-cols-[70px_110px_1fr] gap-2 sm:grid-cols-[70px_120px_110px_110px]">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-slate/70">Qté</label>
              <input type="number" min="0.5" step="0.5" value={line.qty}
                onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`line-${i}-qty`}
                className={`${inputCls} h-9 mt-0.5 font-mono`} />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-slate/70">Prix unit. CHF</label>
              <input type="number" min="0" step="0.05" value={line.unit_price}
                onChange={(e) => setLine(i, { unit_price: e.target.value })} data-testid={`line-${i}-price`}
                className={`${inputCls} h-9 mt-0.5 font-mono`} placeholder="0 = offert" />
            </div>
            <div className="col-span-2 grid grid-cols-2 gap-2 sm:col-span-1">
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate/70">Remise</label>
                <select value={line.discount_type || ""}
                  onChange={(e) => setLine(i, { discount_type: e.target.value || null })}
                  data-testid={`line-${i}-discount-type`} className={`${inputCls} h-9 mt-0.5`}>
                  <option value="">Aucune</option>
                  <option value="percent">%</option>
                  <option value="amount">CHF</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate/70">Valeur</label>
                <input type="number" min="0" step="0.05" value={line.discount_value || ""}
                  disabled={!line.discount_type}
                  onChange={(e) => setLine(i, { discount_value: e.target.value })}
                  data-testid={`line-${i}-discount-value`} className={`${inputCls} h-9 mt-0.5 font-mono disabled:opacity-40`} />
              </div>
            </div>
            {vatRegistered && (
              <div className="hidden sm:block">
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate/70">TVA %</label>
                <input type="number" min="0" step="0.1" value={line.vat_rate ?? ""} placeholder="défaut"
                  onChange={(e) => setLine(i, { vat_rate: e.target.value === "" ? null : Number(e.target.value) })}
                  data-testid={`line-${i}-vat`} className={`${inputCls} h-9 mt-0.5 font-mono`} />
              </div>
            )}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...lines, emptyLine()])} data-testid="line-add"
        className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-ink/15 px-4 py-2.5 text-sm font-medium text-slate transition-colors hover:border-miles hover:text-miles">
        <Plus className="h-4 w-4" strokeWidth={1.5} /> Ajouter une ligne
      </button>
    </div>
  );
}

// Aperçu live du document (HTML — le PDF officiel est généré côté serveur)
export function DocPreview({ kind, number, customer, lines, totals, settings, validUntil, dueDate, serviceInfo, vatRegistered }) {
  const company = settings?.company || {};
  const bank = settings?.bank || {};
  const title = kind === "quote" ? "DEVIS" : "FACTURE";
  return (
    <div className="rounded-2xl border border-ink/[0.07] bg-white p-6 text-ink" data-testid="doc-preview"
      style={{ fontFamily: "Helvetica, Arial, sans-serif" }}>
      <div className="flex items-start justify-between">
        <img src="/brand/logo-blue.png" alt="MILES" className="h-7 w-auto" />
        <div className="text-right">
          <p className="text-xl font-bold tracking-tight">{title}</p>
          <p className="text-xs font-semibold text-ink">{number || "—"}</p>
        </div>
      </div>
      <div className="mt-1 h-0.5 w-full bg-ink" />
      <div className="mt-5 grid grid-cols-2 gap-4 text-[11px]">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate">Émetteur</p>
          <p className="mt-1 font-bold">{company.brand_name || "MILES"}</p>
          <p className="text-slate">{company.legal_name}</p>
          <p className="text-slate">{[bank.street, bank.house_num].filter(Boolean).join(" ")}</p>
          <p className="text-slate">{[bank.pcode, bank.city].filter(Boolean).join(" ")}</p>
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate">{kind === "quote" ? "À l'attention de" : "Facturé à"}</p>
          <p className="mt-1 font-bold">{customer?.name || "—"}</p>
          <p className="text-slate">{[customer?.street, customer?.house_num].filter(Boolean).join(" ") || customer?.email || ""}</p>
          <p className="text-slate">{[customer?.pcode, customer?.city].filter(Boolean).join(" ")}</p>
        </div>
      </div>
      {serviceInfo && (serviceInfo.route || serviceInfo.ride_date) && (
        <div className="mt-4 rounded-lg bg-ink p-3 text-[10px] text-white/60">
          {serviceInfo.route && <p><span className="font-semibold text-white">Trajet</span> · {serviceInfo.route}</p>}
          {serviceInfo.ride_date && <p className="mt-0.5"><span className="font-semibold text-white">Date</span> · {serviceInfo.ride_date}</p>}
        </div>
      )}
      <table className="mt-5 w-full text-[11px]">
        <thead>
          <tr className="bg-ink text-white">
            <th className="rounded-l-md px-3 py-2 text-left font-semibold">Description</th>
            <th className="px-2 py-2 text-right font-semibold">Qté</th>
            <th className="px-2 py-2 text-right font-semibold">Prix unit.</th>
            <th className="rounded-r-md px-3 py-2 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-ink/[0.05]">
              <td className="px-3 py-2.5">
                {l.description || "—"}
                {l._disc > 0 && <p className="text-[10px] text-emerald-600">Remise −{money(l._disc)}</p>}
              </td>
              <td className="px-2 py-2.5 text-right font-mono text-slate">{l.qty}</td>
              <td className="px-2 py-2.5 text-right font-mono text-slate">{Number(l.unit_price) === 0 ? "Offert" : money(l.unit_price)}</td>
              <td className="px-3 py-2.5 text-right font-mono font-semibold">{money(l._total || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 ml-auto w-56 space-y-1 text-[11px]">
        <div className="flex justify-between text-slate"><span>Sous-total HT</span><span className="font-mono">{money(totals.gross ?? totals.net)}</span></div>
        {totals.discount > 0 && (
          <>
            <div className="flex justify-between text-slate"><span>Remise</span><span className="font-mono">−{money(totals.discount)}</span></div>
            <div className="flex justify-between text-slate"><span>Net HT</span><span className="font-mono">{money(totals.net)}</span></div>
          </>
        )}
        {vatRegistered
          ? <div className="flex justify-between text-slate"><span>TVA</span><span className="font-mono">{money(totals.vat)}</span></div>
          : <div className="text-[10px] text-slate">TVA non applicable — entreprise non assujettie.</div>}
        <div className="flex justify-between rounded-md bg-[#F2F4F7] px-3 py-2 text-ink">
          <span className="font-bold">{kind === "quote" ? "Total proposé" : "Total TTC"}</span>
          <span className="font-mono font-bold">{money(totals.total)}</span>
        </div>
      </div>
      {(validUntil || dueDate) && (
        <p className="mt-4 text-[10px] text-slate">
          {validUntil && `Valable jusqu'au ${validUntil}`}{dueDate && `Échéance : ${dueDate}`}
        </p>
      )}
      <p className="mt-3 text-center text-[9px] text-slate/70">
        {[company.legal_name, company.email, company.website].filter(Boolean).join("  ·  ")}
      </p>
    </div>
  );
}
